import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { handleUnauthorizedResponse } from '../../api/http';
import PolicyApi from '../../api/PolicyApi';
import { AuthData } from '../../context/auth';
import { PolicyData, withPolicyContext } from '../../context/policy';
import {
	AutomatedUnderwritingResult,
	PolicyPriceResponse,
} from '../../types/policy';
import { policyIsWithdrawn } from '../../util/util';
import {
	StyledContainer,
	StyledSectionContainer,
	StyledTitle,
} from '../common';
import LargeCenterSpinner from '../LargeCenterSpinner';
import { errorToast } from '../Toast';
import PricePreviewTables from './QuotePricePreviewModal/PricePreviewTables';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

const PolicyPricePreview = (props: Props) => {
	const [priceData, setPriceData] = useState<PolicyPriceResponse>(null);
	const [underwritingData, setUnderwriting] =
		useState<AutomatedUnderwritingResult>(null);

	const { authData, policyData } = props;

	const { policy, productConfig, selectedQuote } = policyData;

	const authToken = authData.auth.authorizationToken;
	const { locator: policyLocator } = policy;
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const source = axios.CancelToken.source();

		const load = async () => {
			setLoading(true);
			try {
				let priceResponse;
				if (selectedQuote) {
					priceResponse = await PolicyApi.getPolicyPricing(
						authToken,
						policyLocator,
						{ cancelToken: source.token },
					);
				} else {
					priceResponse = await PolicyApi.priceQuote(authToken, policyLocator, {
						cancelToken: source.token,
					});
				}
				const underWritingResponse =
					await PolicyApi.getAutomatedUnderwritingResult(
						authToken,
						policyLocator,
						{ cancelToken: source.token },
					);

				if (priceResponse && underWritingResponse) {
					setPriceData(priceResponse.data);
					setUnderwriting(underWritingResponse.data);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
					errorToast('Failed to fetch necessary pricing data');
				}
			}

			setLoading(false);
		};

		if (!policyIsWithdrawn(policy)) {
			load();
		} else {
			setLoading(false);
		}

		return () => {
			source.cancel();
		};
	}, []);

	const renderPriceTables = () => {
		if (!priceData || !underwritingData) {
			return <LargeCenterSpinner />;
		}

		return (
			<PricePreviewTables
				productConfig={productConfig}
				priceData={priceData}
				underwritingData={underwritingData}
			/>
		);
	};

	const renderNoPricing = () => {
		return (
			<div style={{ textAlign: 'center' }}>
				No pricing available for withdrawn policy
			</div>
		);
	};

	if (loading) {
		return <LargeCenterSpinner />;
	}

	return (
		<>
			<StyledContainer>
				<StyledTitle>Pricing Breakdown</StyledTitle>
			</StyledContainer>
			<hr />
			<StyledSectionContainer>
				{policyIsWithdrawn(policy) ? renderNoPricing() : renderPriceTables()}
			</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(PolicyPricePreview);
